import type { Path } from 'react-router';

import { matchAppRoute, matchPathPrefix, matchUnauthenticatedRoute } from './internal';

export { matchAppRoute, matchPathPrefix };

export function matchApprovalDashboard(path: Path) {
  return matchAppRoute('/projects/:projectKey/approvals', path);
}

export function matchApprovalDetails(path: Path) {
  return matchAppRoute('/projects/:projectKey/approvals/:approvalRequestId', path);
}

export function matchFlagApproval(path: Path) {
  return matchAppRoute('/projects/:projectKey/flags/:flagKey/approvals/:approvalRequestId', path);
}

export function matchFlagTargetingScheduledChanges(path: Path) {
  return matchAppRoute('/projects/:projectKey/flags/:flagKey/targeting/pending-changes', path);
}

export function matchFlagTargetingScheduledChange(path: Path) {
  return matchAppRoute('/projects/:projectKey/flags/:flagKey/targeting/pending-changes/:pendingId', path);
}

export function matchFlagVariationsScheduledChanges(path: Path) {
  return matchAppRoute('/projects/:projectKey/flags/:flagKey/variations/pending-changes', path);
}

export function matchFlagVariationScheduledChange(path: Path) {
  return matchAppRoute('/projects/:projectKey/flags/:flagKey/variations/pending-changes/:pendingId', path);
}

export function matchFlagTargetingConflict(path: Path) {
  return matchAppRoute('/projects/:projectKey/flags/:flagKey/targeting/conflicts/:conflictIndex', path);
}

export function matchFlagVariationConflict(path: Path) {
  return matchAppRoute('/projects/:projectKey/flags/:flagKey/variations/conflicts/:conflictIndex', path);
}

export function matchExperimentDesign(path: Path) {
  return matchAppRoute('/projects/:projectKey/experiments/:experimentKey/design', path);
}

export function matchExperimentResults(path: Path) {
  return matchAppRoute('/projects/:projectKey/experiments/:experimentKey/results', path);
}

export function matchExperimentsDashboard(path: Path) {
  return matchAppRoute('/projects/:projectKey/experiments', path);
}

export function matchHoldoutDetails(path: Path) {
  return matchAppRoute('/projects/:projectKey/holdouts/:holdoutKey/details', path);
}

export function matchHoldoutsDashboard(path: Path) {
  return matchAppRoute('/projects/:projectKey/holdouts', path);
}

export function matchMABResults(path: Path) {
  return matchAppRoute('/projects/:projectKey/multi-armed-bandits/:multiArmedBanditKey/results', path);
}

export function matchMABDashboard(path: Path) {
  return matchAppRoute('/projects/:projectKey/multi-armed-bandits', path);
}

export function matchWorkflows(path: Path) {
  return matchAppRoute('/projects/:projectKey/flags/:flagKey/workflows', path);
}

export function matchWorkflow(path: Path) {
  return matchAppRoute('/projects/:projectKey/flags/:flagKey/workflows/:workflowId', path);
}

export function matchFlags(path: Path) {
  return matchAppRoute('/projects/:projectKey/flags', path);
}

export function matchFlag(path: Path) {
  return matchAppRoute('/projects/:projectKey/flags/:flagKey', path);
}

export function matchFlagTargeting(path: Path) {
  return matchAppRoute('/projects/:projectKey/flags/:flagKey/targeting', path);
}

export function matchFlagAddToCode(path: Path) {
  return matchAppRoute('/projects/:projectKey/flags/:flagKey/add-to-code', path);
}

export function matchFlagCode(path: Path) {
  return matchAppRoute('/projects/:projectKey/flags/:flagKey/code', path);
}

export function matchContexts(path: Path) {
  return matchAppRoute('/projects/:projectKey/contexts', path);
}

export function matchContext(path: Path) {
  return matchAppRoute('/projects/:projectKey/contexts', path);
}

export function matchContextDetails(path: Path) {
  return matchAppRoute('/projects/:projectKey/contexts/kinds/:kind/instances/:key', path);
}

export function matchSegments(path: Path) {
  return matchAppRoute('/projects/:projectKey/segments', path);
}

export function matchExperiments(path: Path) {
  return matchAppRoute('/projects/:projectKey/experiments', path);
}

export function matchLive(path: Path) {
  return matchAppRoute('/projects/:projectKey/live', path);
}

export function matchMetrics(path: Path) {
  return matchAppRoute('/projects/:projectKey/metrics/:metricKey/details', path);
}

export function matchAccountHistory(path: Path) {
  return matchAppRoute('/settings/history/*', path);
}

export function matchNewHires(path: Path) {
  return matchAppRoute('/internal/new-hires', path);
}

export function matchFlagCreation(path: Path) {
  return matchAppRoute('/projects/:projectKey/flags/new', path);
}

export function matchBilling(path: Path) {
  return matchAppRoute('/settings/billing', path);
}

export function matchBillingPlans(path: Path) {
  return matchAppRoute('/settings/billing/plans', path);
}

export function matchBillingCustomizePlan(path: Path) {
  return matchAppRoute('/settings/billing/plans/customize', path);
}

export function matchBillingReview(path: Path) {
  return matchAppRoute('/settings/billing/plans/review', path);
}

export function matchInsights(path: Path) {
  return matchAppRoute('/insights', path);
}

export function matchCreateReleasePipeline(path: Path) {
  return (
    matchAppRoute('/projects/:projectKey/settings/release-pipelines/new', path) ||
    matchAppRoute('/projects/:projectKey/settings/releases/release-pipelines/new', path)
  );
}

export function matchEditReleasePipeline(path: Path) {
  return (
    matchAppRoute('/projects/:projectKey/settings/release-pipelines/:releasePipelineKey', path) ||
    matchAppRoute('/projects/:projectKey/settings/releases/release-pipelines/:releasePipelineKey', path)
  );
}

// This is a new route that redirects to the release settings page
export function matchHiddenReleasePipelines(path: Path) {
  return matchAppRoute('/projects/:projectKey/settings/releases/release-pipelines', path);
}

export function matchPayloadFilterEdit(path: Path) {
  return matchAppRoute('/projects/:projectKey/settings/payload-filters/:filterKey/edit', path);
}

export function matchFlagTargetingEventConfiguration(path: Path) {
  return matchAppRoute('/projects/:projectKey/flags/:flagKey/environments/exclude-rules', path);
}

export function matchFlagEnvironmentSettingsEventConfiguration(path: Path) {
  return matchAppRoute('/projects/:projectKey/flags/:flagKey/targeting/exclude-rules', path);
}

export function matchAIConfigVersions(path: Path) {
  return matchAppRoute('/projects/:projectKey/ai-configs/:configKey/variations', path);
}

export function matchAIConfigVariationCompare(path: Path) {
  return matchAppRoute('/projects/:projectKey/ai-configs/:configKey/variations/:variationKey', path);
}

export function matchAIConfigPlayground(path: Path) {
  return matchAppRoute('/projects/:projectKey/ai-configs/:configKey/playground', path);
}

export function matchAIConfigMonitoring(path: Path) {
  return matchAppRoute('/projects/:projectKey/ai-configs/:configKey/monitoring', path);
}

export function matchAITargeting(path: Path) {
  return matchAppRoute('/projects/:projectKey/ai-configs/:configKey/targeting', path);
}

export function matchEventExplorerExpand(path: Path) {
  return matchAppRoute('/projects/:projectKey/observe/expand/:dataSourceKey/:eventKind/:eventKey', path);
}

export function matchAIMonitoringTrends(path: Path) {
  return matchAppRoute('/projects/:projectKey/ai-monitoring/trends', path);
}

export function matchViewsSettings(path: Path) {
  return matchAppRoute('/projects/:projectKey/settings/views/settings', path);
}

export function matchViewCreate(path: Path) {
  return matchAppRoute('/projects/:projectKey/settings/views/new', path);
}

export function matchViewAndChildren(path: Path) {
  if (matchViewsSettings(path) || matchViewCreate(path)) {
    return false;
  }
  return (
    matchView(path) ||
    matchViewSettings(path) ||
    matchViewLinkedResources(path) ||
    matchViewLinkedResourcesAdd(path) ||
    matchViewLinkedResource(path) ||
    matchViewApplication(path)
  );
}

export function matchProjectSettingsFullScreenFlow(path: Path) {
  return (
    matchCreateReleasePipeline(path) ||
    matchEditReleasePipeline(path) ||
    matchPayloadFilterEdit(path) ||
    matchViewAndChildren(path)
  );
}

export function matchViewLinkedResources(path: Path) {
  return matchAppRoute('/projects/:projectKey/settings/views/:viewKey/linked-resources', path);
}

export function matchViewLinkedResource(path: Path) {
  return matchAppRoute('/projects/:projectKey/settings/views/:viewKey/linked-resources/:resource', path);
}

export function matchViewLinkedResourcesAdd(path: Path) {
  return matchAppRoute('/projects/:projectKey/settings/views/:viewKey/linked-resources/:resource/add', path);
}

export function matchViewApplication(path: Path) {
  return matchAppRoute('/projects/:projectKey/settings/views/:viewKey/application', path);
}

export function matchView(path: Path) {
  return matchAppRoute('/projects/:projectKey/settings/views/:viewKey', path);
}

export function matchViewSettings(path: Path) {
  return matchAppRoute('/projects/:projectKey/settings/views/:viewKey/settings', path);
}

export function matchProjectEnvironmentKeys(path: Path) {
  return matchAppRoute('/projects/:projectKey/settings/environments/:envKey/keys', path);
}

export function matchSdkSetup(path: Path) {
  return matchAppRoute('/projects/:projectKey/sdk-setup', path);
}

export function matchHome(path: Path) {
  return matchAppRoute('/projects/:projectKey/home', path);
}

export function matchAuthorizationRoot(path: Path) {
  return matchAppRoute('/settings/authorization', path);
}

/** Tokenless pre-auth welcome (cookie onboarding flow). Does not match `/welcome/:token`. */
export function matchTokenlessWelcome(path: Path) {
  return matchUnauthenticatedRoute('/welcome', path);
}

/** Tokenless pre-auth join-org (cookie onboarding flow). Does not match `/join-your-organization/:token`. */
export function matchTokenlessJoinOrganization(path: Path) {
  return matchUnauthenticatedRoute('/join-your-organization', path);
}

/** Tokenless pre-auth pending-invite (cookie onboarding flow). Does not match `/pending-invite/:token`. */
export function matchTokenlessPendingInvite(path: Path) {
  return matchUnauthenticatedRoute('/pending-invite', path);
}
